import { jwtDecode } from "jwt-decode";

let refreshPromise = null;
let onTokenChangeCallback = null;

export function onTokenChange(callback) {
    onTokenChangeCallback = callback;
}

function notifyTokenChange() {
    if (onTokenChangeCallback) {
        const user = setup();
        onTokenChangeCallback(user || null);
    }
}

export function logout() {
    window.localStorage.removeItem("authToken");
    window.localStorage.removeItem("refreshToken");
}

export function authenticate(credentials) {

    const URL_LOGIN = `${process.env.REACT_APP_API_URL}/api/login`

    return fetch(URL_LOGIN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
    })
        .then(async response => {
            if (!response.ok) {
                const data = await response.json().catch(() => null);
                const error = new Error(data?.message || "Authentication failed");
                error.status = response.status;
                throw error;
            }
            return response.json();
        })
        .then(data => {
            window.localStorage.setItem("authToken", data.token);
            window.localStorage.setItem("refreshToken", data.refresh_token);
            notifyTokenChange();
            return true;
        });
}

function refreshAuthToken() {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
        try {
            const refreshToken = window.localStorage.getItem("refreshToken");
            if (!refreshToken) throw new Error("No refresh token");

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/token/refresh`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ refresh_token: refreshToken }),
            });

            if (!response.ok) throw new Error("Token refresh failed");

            const data = await response.json();
            window.localStorage.setItem("authToken", data.token);
            window.localStorage.setItem("refreshToken", data.refresh_token);
            notifyTokenChange();
            return data.token;
        } finally {
            refreshPromise = null;
        }
    })();

    return refreshPromise;
}

export async function authFetch(url, options = {}) {
    let token = window.localStorage.getItem("authToken");

    if (token) {
        const { exp } = jwtDecode(token);
        if (exp <= Date.now() / 1000) {
            try {
                token = await refreshAuthToken();
            } catch {
                logout();
                window.location.href = "/login";
                throw new Error("Session expired");
            }
        }
    }

    const response = await fetch(url, {
        ...options,
        headers: {
            ...options.headers,
            ...(token ? { Authorization: "Bearer " + token } : {}),
        },
    });

    if (response.status === 401) {
        try {
            token = await refreshAuthToken();
        } catch {
            logout();
            window.location.href = "/login";
            throw new Error("Session expired");
        }
        return fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                Authorization: "Bearer " + token,
            },
        });
    }

    return response;
}

export function setup() {
    const token = window.localStorage.getItem("authToken");
    if (token) {
        const { id, username, role, firstName, lastName, permissions, exp: expiration } = jwtDecode(token);
        const refreshToken = window.localStorage.getItem("refreshToken");
        if (refreshToken || expiration > new Date().getTime() / 1000) {
            return { id, username, role, firstName, lastName, permissions: permissions || [] };
        }
    }
}

export function forgotPassword(email) {
    return fetch(`${process.env.REACT_APP_API_URL}/api/password/forgot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
    }).then(response => {
        if (!response.ok) {
            throw new Error("Échec de la demande");
        }
    });
}

export function resetPassword(token, plainPassword) {
    return fetch(`${process.env.REACT_APP_API_URL}/api/password/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, plainPassword }),
    }).then(response => {
        if (!response.ok) {
            const error = new Error("Échec de la réinitialisation");
            error.status = response.status;
            throw error;
        }
    });
}

export function setPassword(token, password) {
    return fetch(`${process.env.REACT_APP_API_URL}/api/invitations/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, plainPassword: password }),
    }).then(response => {
        if (!response.ok) {
            throw new Error("Échec de la définition du mot de passe");
        }
        return response.json();
    });
}

export function isAuthenticated() {
    const refreshToken = window.localStorage.getItem("refreshToken");
    if (refreshToken) return true;
    const token = window.localStorage.getItem("authToken");
    if (token) {
        const { exp: expiration } = jwtDecode(token);
        if (expiration > new Date().getTime() / 1000) {
            return true;
        }
    }
    return false;
}
